import {
	OrderAdjustmentCalculationType,
	OrderAdjustmentType,
	SamplePaymentTreatment,
} from '@codezo/graphql';
import {
	AdjustmentsSection,
	type ExtrasAdjustment,
	type ExtrasProduct,
	type SectionFrame,
} from '@codezo/offer-extras';
import type { ReactNode } from 'react';
import { CurrencyText } from '../../CurrencyText';
import { treatmentMarker } from '../../../lib/sample-treatment';
import type { Batch, QuoteItem } from './types';

/**
 * The admin card's samples / adjustments sections.
 *
 * The layout lives in `@codezo/offer-extras`, shared with the client card so the
 * two can't drift. This file is the adapter: it flattens the admin's offer shape
 * into the shared contract, supplies the app's own primitives, and passes the
 * authoring actions that turn each section editable.
 *
 * Editable sections list EVERY product, including the ones with nothing set —
 * that row is where Add lives, and it's the only way to put a sample on a
 * product that hasn't got one.
 *
 * Attachments are NOT here. They belong to the product, and the product row on
 * this same page already lists and manages them — see `QuoteItemFiles`. The
 * client card still has an attachments section, because that is the only place a
 * client can see the files at all.
 */

export function toExtrasProducts(
	batch: Batch,
	itemLookup: Map<string, QuoteItem>,
	/** Products the card has deselected. They keep their rows — admin has to be
	 * able to author a sample on a product it isn't currently pricing — but the
	 * rows read as excluded and nothing on them can be picked. */
	excludedOfferIds: Set<string>
): ExtrasProduct[] {
	return batch.offers.map((offer) => {
		const sample = offer.sample;
		// A waived sample carries no prices at all — nothing was set and nothing is
		// charged — so the authoring breakdown has nothing to show. totalPrice
		// returns 0 server-side, so it needs no help here.
		const isWaived = sample?.paymentTreatment === SamplePaymentTreatment.Waived;
		return {
			offerId: offer.id,
			itemId: offer.orderQuoteItemId,
			name: itemLookup.get(offer.orderQuoteItemId)?.productName || offer.name,
			excluded: excludedOfferIds.has(offer.id),
			sample: sample
				? {
						id: sample.id,
						totalPrice: sample.totalPrice ?? null,
						minProductionDays: sample.minProductionDays ?? null,
						maxProductionDays: sample.maxProductionDays ?? null,
						deliveryDaysMin: sample.deliveryDaysMin ?? null,
						deliveryDaysMax: sample.deliveryDaysMax ?? null,
						notes: sample.notes,
						treatmentNote: treatmentMarker(sample.paymentTreatment),
						// One total per choice, so each option tile prices itself rather than
						// all three quoting the with-shipping figure.
						pricesByChoice: sample.pricesByChoice,
						// What the total is made of — admin-only, and behind a hover
						// rather than three columns of its own, since only the person
						// authoring the sample needs it.
						breakdown: isWaived
							? null
							: [
									{
										label: 'Sample price',
										amount: sample.baseSamplePrice ?? null,
									},
									{ label: 'Shipping', amount: sample.baseShippingPrice ?? null },
								],
					}
				: null,
			adjustments: (offer.adjustments ?? []).map((adj) => ({
				id: adj.id,
				name: adj.name,
				description: adj.description,
				isFee: adj.type === OrderAdjustmentType.Fee,
				isPercentage: adj.calculationType === OrderAdjustmentCalculationType.Percentage,
				value: adj.value,
			})),
			attachments: (offer.attachments ?? []).map((f) => ({
				id: f.id,
				name: f.name,
				description: f.description,
				mimeType: f.mimeType,
				size: f.size,
				url: f.url,
			})),
		};
	});
}

export function AdminAdjustmentsSection({
	products,
	canEdit,
	setEditAdjTarget,
	setRemoveAdjTarget,
	action,
	frame,
}: {
	products: ExtrasProduct[];
	canEdit: boolean;
	setEditAdjTarget: (adj: ExtrasAdjustment) => void;
	setRemoveAdjTarget: (v: { id: string; name: string }) => void;
	/** The header's add control. Built by the card, which knows the batch. */
	action?: ReactNode;
	frame?: SectionFrame;
}) {
	return (
		<AdjustmentsSection
			products={products}
			CurrencyText={CurrencyText}
			frame={frame}
			action={action}
			actions={
				canEdit
					? {
							onEdit: setEditAdjTarget,
							onRemove: (id, name) => setRemoveAdjTarget({ id, name }),
						}
					: undefined
			}
		/>
	);
}
